import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import welcomeBg from "../../assets/homebg.png";

import FlameIcon from "../../assets/Flame1.svg";
import DetailImage from "../../assets/food.png";

interface FoodDetailProps {
  id: string;
  name: string;
  calories: number;
  totalFats: number;
  transFats: number;
  saturatedFats: number;
  cholesterol: number;
  netCarbs: number;
  dietaryFibers: number;
  totalGI: number;
  totalGL: number;
  proteins: number;
}

const MealInDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [foodItem, setFoodItem] = useState<FoodDetailProps | null>(null);
  const mealImage = location.state?.mealImage; // Get meal image from navigation state
  const mealDate = location.state?.mealDate; // Get meal date from API (e.g., "2025-11-05 17:47:08")

  useEffect(() => {
    if (location.state?.foodItem) {
      setFoodItem(location.state.foodItem);
    } else {
      setFoodItem({
        id: "1",
        name: "Dosa Sambar Chutney",
        calories: 1100,
        totalFats: 30,
        transFats: 0,
        saturatedFats: 0,
        cholesterol: 50,
        netCarbs: 0,
        dietaryFibers: 0,
        totalGI: 80,
        totalGL: 0,
        proteins: 20,
      });
    }
  }, [location.state]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const getMealTime = () => {
    if (!mealDate) {
      // Fallback to current time if meal date not available
      return new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }

    try {
      if (typeof mealDate === "string") {
        if (mealDate.includes(" ")) {
          const timePart = mealDate.split(" ")[1];

          if (timePart?.includes(":")) {
            const timeParts = timePart.split(":");
            const hours = timeParts[0];
            const minutes = timeParts[1];

            if (hours && minutes) {
              const hour24 = parseInt(hours, 10);
              const minute = parseInt(minutes, 10);

              if (
                !isNaN(hour24) &&
                !isNaN(minute) &&
                hour24 >= 0 &&
                hour24 <= 23 &&
                minute >= 0 &&
                minute <= 59
              ) {
                // Convert UTC time to Local time for display
                // API returns UTC time, but user wants to see local time
                const utcDate = new Date(mealDate.replace(" ", "T") + "Z"); // Add Z to indicate UTC
                const localHour24 = utcDate.getHours(); // Get local hour
                const localMinute = utcDate.getMinutes(); // Get local minute

                const hour12 =
                  localHour24 > 12
                    ? localHour24 - 12
                    : localHour24 === 0
                      ? 12
                      : localHour24;
                const ampm = localHour24 >= 12 ? "PM" : "AM";
                const timeString = `${hour12}:${String(localMinute).padStart(2, "0")} ${ampm}`;

                return timeString;
              }
            }
          }
        } else if (mealDate.includes("T")) {
          const timePart = mealDate.split("T")[1]?.split(".")[0];

          if (timePart?.includes(":")) {
            const timeParts = timePart.split(":");
            const hours = timeParts[0];
            const minutes = timeParts[1];

            if (hours && minutes) {
              const hour24 = parseInt(hours, 10);
              const minute = parseInt(minutes, 10);

              if (
                !isNaN(hour24) &&
                !isNaN(minute) &&
                hour24 >= 0 &&
                hour24 <= 23 &&
                minute >= 0 &&
                minute <= 59
              ) {
                // Convert UTC time to Local time for display
                // If date doesn't have timezone indicator, treat as UTC
                let utcDateString = mealDate;
                if (
                  !utcDateString.includes("Z") &&
                  !utcDateString.includes("+") &&
                  !utcDateString.includes("-", 10)
                ) {
                  utcDateString = utcDateString + "Z"; // Add Z to indicate UTC
                }
                const utcDate = new Date(utcDateString);
                const localHour24 = utcDate.getHours(); // Get local hour
                const localMinute = utcDate.getMinutes(); // Get local minute

                const hour12 =
                  localHour24 > 12
                    ? localHour24 - 12
                    : localHour24 === 0
                      ? 12
                      : localHour24;
                const ampm = localHour24 >= 12 ? "PM" : "AM";
                const timeString = `${hour12}:${String(localMinute).padStart(2, "0")} ${ampm}`;
                return timeString;
              }
            }
          }
        }
      }

      const parsedDate = new Date(mealDate);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
    } catch (_error) {
      // Fallback to current time if date parsing fails
    }

    return new Date().toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (!foodItem) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/90" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header Card */}
        <div className="rounded-2xl border border-border bg-card p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBackClick}
              className="flex h-8 w-8 items-center justify-center rounded-xl hover:bg-muted"
            >
              <ArrowLeftOutlined className="text-title2" />
            </button>

            <div className="text-subhead font-medium text-foreground">
              {getMealTime()}
            </div>
          </div>
        </div>

        <div className="mb-6 px-4">
          <div className="flex h-64 w-full items-center justify-center overflow-hidden rounded-2xl bg-[#2A2A2A]">
            {mealImage ? (
              <img
                src={mealImage}
                alt={foodItem.name}
                className="h-full w-full object-cover"
                onError={e => {
                  const target = e.target as HTMLImageElement;
                  target.src = DetailImage;
                }}
              />
            ) : (
              <img
                src={DetailImage}
                alt={foodItem.name}
                className="h-full w-full object-cover"
                onError={e => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  target.parentElement!.innerHTML = `
                    <div class="text-white text-center">
                      <div class="text-4xl mb-2">🥘</div>
                      <div class="text-subhead opacity-70">No Image Available</div>
                    </div>
                  `;
                }}
              />
            )}
          </div>
        </div>

        {/* Food Name Card */}
        <div className="rounded-2xl border border-border bg-card p-4 shadow-lg">
          <h1 className="text-title1 font-bold text-foreground">
            {foodItem.name}
          </h1>
        </div>

        {/* Calories Card */}
        <div className="rounded-2xl border border-border bg-card p-4 shadow-lg">
          <div className="mb-2 flex items-center gap-3">
            <img src={FlameIcon} alt="Calories" className="h-6 w-6" />
            <span className="text-title3 font-semibold text-foreground">
              Calories
            </span>
          </div>
          <div className="text-large-title font-bold text-foreground">
            {Math.round(foodItem.calories)}
          </div>
        </div>

        {/* Nutrition Facts Card */}
        <div className="rounded-2xl border border-border bg-card p-4 shadow-lg">
          <h2 className="mb-4 text-title2 font-bold text-foreground">
            Nutrition Facts
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-callout">Calories</span>
              <span className="text-callout font-medium text-foreground">
                {Math.round(foodItem.calories)} Cal
              </span>
            </div>

            <div className="h-px bg-border" />

            <div className="flex items-center justify-between">
              <span className="text-callout">Total Fats</span>
              <span className="text-callout font-medium text-foreground">
                {Math.round(foodItem.totalFats * 10) / 10}g
              </span>
            </div>

            <div className="flex items-center justify-between pl-4">
              <span className="text-subhead text-muted-foreground">Trans Fats</span>
              <span className="text-subhead text-muted-foreground">
                {Math.round(foodItem.transFats * 10) / 10}g
              </span>
            </div>

            {/* Saturated Fats */}
            <div className="flex items-center justify-between pl-4">
              <span className="text-subhead text-muted-foreground">
                Saturated Fats
              </span>
              <span className="text-subhead text-muted-foreground">
                {Math.round(foodItem.saturatedFats * 10) / 10}g
              </span>
            </div>

            <div className="h-px bg-border" />

            <div className="flex items-center justify-between">
              <span className="text-callout">Cholesterol</span>
              <span className="text-callout font-medium text-foreground">
                {Math.round(foodItem.cholesterol * 10) / 10}g
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-callout">Net Carbs</span>
              <span className="text-callout font-medium text-foreground">
                {Math.round(foodItem.netCarbs * 10) / 10}g
              </span>
            </div>

            <div className="flex items-center justify-between pl-4">
              <span className="text-subhead text-muted-foreground">
                Dietary Fibers
              </span>
              <span className="text-subhead text-muted-foreground">
                {Math.round(foodItem.dietaryFibers * 10) / 10}g
              </span>
            </div>

            <div className="h-px bg-border" />

            <div className="flex items-center justify-between">
              <span className="text-callout">Total GI</span>
              <span className="text-callout font-medium text-foreground">
                {Math.round(foodItem.totalGI)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-callout">Total GL</span>
              <span className="text-callout font-medium text-foreground">
                {Math.round(foodItem.totalGL * 10) / 10}
              </span>
            </div>

            <div className="h-px bg-border" />

            <div className="flex items-center justify-between">
              <span className="text-callout">Proteins</span>
              <span className="text-callout font-medium text-foreground">
                {Math.round(foodItem.proteins * 10) / 10}g
              </span>
            </div>
          </div>
        </div>

        <div className="h-20" />
      </div>
    </div>
  );
};

export default MealInDetailPage;
